import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import AdminLayout from "@/components/admin/AdminLayout";
import { AdminFilterBar } from "@/components/admin/AdminSurface";
import UserFilters from "@/components/admin/UserFilters";
import UserManagementTable from "@/components/admin/UserManagementTable";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth";
import { deleteUserAccount } from "@/contexts/auth/utils/accountManagement";
import { sendAccountInactiveEmail } from "@/utils/supabase/notificationHelpers";
import { getAllUsersWithRoles } from "@/services/profileService";
import { setUserAdminStatus, setUserActiveStatus } from "@/utils/supabase/profileHelpers";
import { ProfileData } from "@/types/supabase";

interface UserWithRoles extends ProfileData {
  isAdmin: boolean;
  isActive: boolean;
}

const UserManagementPage = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [roleFilter, setRoleFilter] = useState<"all" | "admin" | "user">("all");

  const { data: users, isLoading, isError, refetch } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const data = await getAllUsersWithRoles();
      return data as UserWithRoles[];
    },
    enabled: isAdmin,
  });

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return (users ?? [])
      .filter((user) => {
        const haystack = [user.first_name, user.last_name, user.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const matchesSearch = !normalizedSearch || haystack.includes(normalizedSearch);
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" && user.isActive) ||
          (statusFilter === "inactive" && !user.isActive);
        const matchesRole =
          roleFilter === "all" ||
          (roleFilter === "admin" && user.isAdmin) ||
          (roleFilter === "user" && !user.isAdmin);

        return matchesSearch && matchesStatus && matchesRole;
      })
      .sort((left, right) => left.email.localeCompare(right.email));
  }, [roleFilter, searchTerm, statusFilter, users]);

  const counts = useMemo(
    () => ({
      total: users?.length ?? 0,
      active: users?.filter((user) => user.isActive).length ?? 0,
      inactive: users?.filter((user) => !user.isActive).length ?? 0,
      admins: users?.filter((user) => user.isAdmin).length ?? 0,
    }),
    [users],
  );

  const handleRoleChange = async (userId: string, makeAdmin: boolean) => {
    try {
      const success = await setUserAdminStatus(userId, makeAdmin);

      if (!success) {
        throw new Error("Operation failed. Please try again.");
      }

      toast({
        title: makeAdmin ? "User promoted" : "User demoted",
        description: makeAdmin
          ? "The user now has administrator access."
          : "The user has been returned to a regular account.",
      });

      void refetch();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update user role.";
      toast({
        title: "Error updating user role",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (userId: string, makeActive: boolean) => {
    try {
      const success = await setUserActiveStatus(userId, makeActive);

      if (!success) {
        throw new Error("Operation failed. Please try again.");
      }

      let notificationSent = true;

      if (!makeActive) {
        notificationSent = await sendAccountInactiveEmail(userId);
      }

      toast({
        title: `User ${makeActive ? "activated" : "deactivated"}`,
        description: makeActive
          ? "The user can sign in again immediately."
          : notificationSent
            ? "The user has been notified and can no longer sign in."
            : "The user can no longer sign in. The status changed, but the notification email could not be sent.",
      });

      void refetch();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update user status.";
      toast({
        title: "Error updating user status",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteUserAccount(userId);

      toast({
        title: "Account deleted",
        description: "The user account and its authentication record have been removed.",
      });

      void refetch();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to delete account.";
      toast({
        title: "Error deleting account",
        description: message,
        variant: "destructive",
      });
    }
  };

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <AdminLayout
      title="Users"
      description="Search, filter, and update account access from one table so support decisions happen in place."
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
          onClick={() => void refetch()}
        >
          Refresh users
        </Button>
      }
    >
      <div className="space-y-6">
        <AdminFilterBar>
          <UserFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            counts={counts}
            onReset={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setRoleFilter("all");
            }}
          />
        </AdminFilterBar>

        <UserManagementTable
          users={filteredUsers}
          onStatusChange={handleStatusChange}
          onRoleChange={handleRoleChange}
          onDeleteUser={handleDeleteUser}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          summary={`Showing ${filteredUsers.length} of ${counts.total} users. Status and role changes remain inline to reduce drill-downs.`}
        />
      </div>
    </AdminLayout>
  );
};

export default UserManagementPage;
